import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { WarehouseService } from '../warehouse.service';

/** One shaded stretch of published hours behind a day column. */
interface CalBand {
	top: number;
	height: number;
	label: string;
}

/** One visit, positioned as a share of the visible day. */
interface CalEvent {
	v: any;
	top: number;
	height: number;
	left: number;
	width: number;
	short: boolean;
	tiny: boolean;
	clipTop: boolean;
	clipBottom: boolean;
}

interface CalDay {
	date: string;
	dow: string;
	daynum: number;
	isToday: boolean;
	isPast: boolean;
	isWeekend: boolean;
	openSlots: number;
	visitCount: number;
	bands: CalBand[];
	events: CalEvent[];
	allday: any[];
}

/**
 * Warehouse Visits — the ERP console for the visit scheduler.
 *
 * Brands and dispensaries raise visits from the B2B portal; this is where the
 * warehouse answers them. A week time grid shows what is booked against the
 * hours the warehouse has published, pending pickup requests sit above it
 * waiting for a real time, and the same screen blocks time off and edits the
 * published hours.
 *
 * The asymmetry the whole scheduler is built on: a drop-off is a BOOKING (the
 * visitor took a published slot, it is already confirmed and it consumes
 * capacity), a pickup is a REQUEST (a preferred time only, holding nothing
 * until somebody here confirms it). That is why a request gets Confirm/Decline
 * and a booking gets Came/No show.
 *
 * Layout is worked out here rather than server-side so changing the warehouse
 * filter re-lays the week out without a round trip. It mirrors the arithmetic
 * in apir/warehouse/visits.php and the b2b portal's own calendar — a rule
 * change has to be made in all three.
 */
@Component({
	selector: 'app-warehouse-visits',
	templateUrl: './warehouse-visits.component.html',
	styleUrls: ['./warehouse-visits.component.scss']
})
export class WarehouseVisitsComponent implements OnInit {
	@ViewChild('blockDialog', { static: true }) blockDialog!: TemplateRef<any>;

	/** Shortest visible day the grid will draw, in whole hours. */
	private readonly MIN_HOURS = 9;

	loading = false;
	saving = false;
	message = '';
	messageOk = false;

	weekstart = '';
	weekend = '';
	loccode = '';
	locations: any[] = [];

	visits: any[] = [];
	requests: any[] = [];
	windows: any[] = [];
	weekly: any[] = [];
	oneoffs: any[] = [];
	openSlots: Record<string, number> = {};

	days: CalDay[] = [];
	hourMarks: { hour: number; label: string }[] = [];
	gridStart = 480;
	gridSpan = 540;
	hasAllday = false;
	nowDate = '';
	nowPct = -1;

	selected: any = null;
	showHours = false;

	/**
	 * The one hours editor. `replaceDays` is the day set the schedule being
	 * edited started with — the only days the save may remove, so editing a
	 * "Friday 9-2" line can never reconcile away the Mon-Thu one.
	 */
	editing = false;
	editTitle = '';
	editMode: 'week' | 'date' = 'week';
	editDays: Record<number, boolean> = {};
	replaceDays: number[] = [];

	blockForm: UntypedFormGroup;
	hoursForm: UntypedFormGroup;
	confirmForm: UntypedFormGroup;
	moveForm: UntypedFormGroup;
	internalNote = '';

	/** Monday first: a week of day pills that starts on Sunday reads wrong. */
	weekdayOptions = [
		{ value: 1, short: 'Mon' },
		{ value: 2, short: 'Tue' },
		{ value: 3, short: 'Wed' },
		{ value: 4, short: 'Thu' },
		{ value: 5, short: 'Fri' },
		{ value: 6, short: 'Sat' },
		{ value: 0, short: 'Sun' }
	];

	constructor(
		private warehouseService: WarehouseService,
		private fb: UntypedFormBuilder,
		private dialog: MatDialog
	) {
		this.blockForm = this.fb.group({
			loccode: ['', Validators.required],
			title: [''],
			starts_at: ['', Validators.required],
			ends_at: ['', Validators.required]
		});
		this.hoursForm = this.fb.group({
			loccode: ['', Validators.required],
			visit_kind: ['dropoff', Validators.required],
			specific_date: [''],
			start_time: ['09:00', Validators.required],
			end_time: ['16:00', Validators.required],
			slot_minutes: [30, [Validators.required, Validators.min(5), Validators.max(480)]],
			capacity: [1, [Validators.required, Validators.min(1), Validators.max(99)]],
			active: [true]
		});
		this.confirmForm = this.fb.group({
			starts_at: ['', Validators.required],
			ends_at: ['', Validators.required],
			note: ['']
		});
		this.moveForm = this.fb.group({
			starts_at: ['', Validators.required],
			ends_at: ['', Validators.required]
		});
	}

	ngOnInit(): void {
		this.weekstart = this.mondayOf(this.today());
		this.load();
	}

	/* ------------------------------------------------------------------ */
	/* Load                                                                */
	/* ------------------------------------------------------------------ */

	load(): void {
		this.loading = true;
		this.selected = null;
		this.warehouseService.getWarehouseVisitWeek(this.weekstart, this.loccode).subscribe((r: any) => {
			this.loading = false;
			this.applyWeek(r);
		});
	}

	/**
	 * Take one /week response and lay the calendar out.
	 *
	 * Split out from load() so the grid can be exercised without the network.
	 */
	applyWeek(r: any): void {
		if (!r || !r.success) {
			this.visits = [];
			this.requests = [];
			this.windows = [];
			this.weekly = [];
			this.oneoffs = [];
			this.openSlots = {};
			this.say(r && r.message ? r.message : 'Could not load the calendar.', false);
			this.buildWeek();
			return;
		}
		this.weekstart = r.weekstart || this.weekstart;
		this.weekend = r.weekend || this.addDays(this.weekstart, 6);
		this.locations = r.locations || [];
		this.visits = r.visits || [];
		this.requests = r.requests || [];
		this.windows = r.windows || [];
		this.weekly = r.weekly || [];
		this.oneoffs = r.oneoffs || [];
		this.openSlots = r.open_slots || {};
		this.buildWeek();
	}

	/* ------------------------------------------------------------------ */
	/* Calendar layout                                                     */
	/* ------------------------------------------------------------------ */

	/**
	 * Turn the week's visits and published hours into a drawable grid.
	 *
	 * A visit crossing midnight is NOT positioned — it becomes an "all day"
	 * chip, the way a real calendar handles one. Without that a single
	 * overnight block would drag the visible range out to the full 24 hours and
	 * squash every real appointment into a sliver.
	 */
	private buildWeek(): void {
		const dates: string[] = [];
		for (let i = 0; i < 7; i++) {
			dates.push(this.addDays(this.weekstart, i));
		}

		const bands: Record<string, CalBand[]> = {};
		const rawBands: Record<string, number[][]> = {};
		const timed: Record<string, any[]> = {};
		const allday: Record<string, any[]> = {};

		dates.forEach(d => {
			rawBands[d] = this.bandsOn(d);
			timed[d] = [];
			allday[d] = [];
		});

		this.visits.forEach(v => {
			dates.forEach(d => {
				// A row belongs to a day when it overlaps it at all, so a block
				// running past midnight shows on both days it touches.
				if (v.date === d && !v.spans_days) {
					timed[d].push({ start_min: v.start_min, end_min: v.end_min, v: v });
					return;
				}
				if (v.spans_days && this.touchesDay(v, d)) {
					allday[d].push(v);
				}
			});
		});

		const bounds = this.gridBounds(rawBands, timed);
		this.gridStart = bounds[0];
		this.gridSpan = bounds[1] - bounds[0];

		this.hourMarks = [];
		for (let h = this.gridStart / 60; h < bounds[1] / 60; h++) {
			this.hourMarks.push({ hour: h, label: this.hourLabel(h) });
		}

		const today = this.today();
		const nowMin = new Date().getHours() * 60 + new Date().getMinutes();

		this.hasAllday = false;
		this.days = dates.map(d => {
			if (allday[d].length) {
				this.hasAllday = true;
			}
			bands[d] = rawBands[d].map(b => {
				const top = Math.max(this.gridStart, Math.min(b[0], this.gridStart + this.gridSpan));
				const bot = Math.max(this.gridStart, Math.min(b[1], this.gridStart + this.gridSpan));
				return {
					top: this.pct(top - this.gridStart),
					height: this.pct(bot - top),
					label: this.clock(b[0]) + ' – ' + this.clock(b[1])
				};
			}).filter(b => b.height > 0);

			const events = this.positionDay(timed[d]);
			const dow = new Date(d + 'T00:00:00').getDay();
			return {
				date: d,
				dow: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dow],
				daynum: Number(d.substring(8, 10)),
				isToday: d === today,
				isPast: d < today,
				isWeekend: dow === 0 || dow === 6,
				openSlots: Number(this.openSlots[d] || 0),
				visitCount: events.length + allday[d].length,
				bands: bands[d],
				events: events,
				allday: allday[d]
			};
		});

		const inGrid = today >= this.weekstart && today <= dates[6]
			&& nowMin >= this.gridStart && nowMin <= this.gridStart + this.gridSpan;
		this.nowDate = inGrid ? today : '';
		this.nowPct = inGrid ? this.pct(nowMin - this.gridStart) : -1;
	}

	/**
	 * Published hours for one date, merged into bands.
	 *
	 * Merging matters: with no warehouse filter the same Monday morning is
	 * published by every site, and stacked translucent bands would read as
	 * darker "more open" hours.
	 */
	private bandsOn(date: string): number[][] {
		const wd = new Date(date + 'T00:00:00').getDay();
		const raw: number[][] = [];

		this.windows.forEach(w => {
			if (!Number(w.active)) { return; }
			if (w.specific_date) {
				if (w.specific_date !== date) { return; }
			} else {
				if (w.weekday === null || Number(w.weekday) !== wd) { return; }
			}
			const s = this.minutesOf(w.start_time);
			const e = this.minutesOf(w.end_time);
			if (e > s) { raw.push([s, e]); }
		});

		raw.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
		const out: number[][] = [];
		raw.forEach(b => {
			const last = out.length ? out[out.length - 1] : null;
			if (last && b[0] <= last[1]) {
				last[1] = Math.max(last[1], b[1]);
			} else {
				out.push([b[0], b[1]]);
			}
		});
		return out;
	}

	/**
	 * Visible hour range, snapped to whole hours.
	 *
	 * Whole hours matter: the hour lines behind the columns are painted by a
	 * repeating gradient over the same span, so a range starting at 08:20 would
	 * put every line 20 minutes off its label.
	 */
	private gridBounds(bands: Record<string, number[][]>, timed: Record<string, any[]>): number[] {
		let lo = 24 * 60;
		let hi = 0;

		Object.keys(bands).forEach(d => bands[d].forEach(b => {
			lo = Math.min(lo, b[0]);
			hi = Math.max(hi, b[1]);
		}));
		Object.keys(timed).forEach(d => timed[d].forEach(s => {
			lo = Math.min(lo, s.start_min);
			hi = Math.max(hi, s.end_min);
		}));

		if (hi <= lo) {
			lo = 8 * 60;
			hi = 17 * 60;
		}
		let sh = Math.floor(lo / 60);
		let eh = Math.ceil(hi / 60);
		// A warehouse day with one 09:00 booking would otherwise render as a
		// single letterbox row. Pad out to a plausible working day.
		if (eh - sh < this.MIN_HOURS) {
			const grow = this.MIN_HOURS - (eh - sh);
			sh = Math.max(0, sh - Math.floor(grow / 2));
			eh = Math.min(24, sh + this.MIN_HOURS);
			sh = Math.max(0, eh - this.MIN_HOURS);
		}
		return [sh * 60, eh * 60];
	}

	/**
	 * Give one day's visits a top, a height and a lane.
	 *
	 * Lanes are assigned per cluster of transitively overlapping visits, not per
	 * day: two appointments that clash at 09:00 take half the column each,
	 * while a lone 15:00 booking still gets the full width.
	 */
	private positionDay(segs: any[]): CalEvent[] {
		const top = this.gridStart;
		const bottom = this.gridStart + this.gridSpan;

		const list = segs.map(s => ({
			v: s.v,
			startMin: s.start_min,
			endMin: s.end_min,
			topMin: Math.max(top, Math.min(s.start_min, bottom)),
			botMin: Math.max(top, Math.min(s.end_min, bottom)),
			lane: 0,
			lanes: 1
		})).filter(s => s.botMin > s.topMin)
			.sort((a, b) => a.topMin - b.topMin || b.botMin - a.botMin);

		const clusters: number[][] = [];
		let cur: number[] = [];
		let curEnd = -1;
		list.forEach((s, i) => {
			if (curEnd >= 0 && s.topMin >= curEnd) {
				clusters.push(cur);
				cur = [];
				curEnd = -1;
			}
			cur.push(i);
			curEnd = Math.max(curEnd, s.botMin);
		});
		if (cur.length) { clusters.push(cur); }

		clusters.forEach(idxs => {
			const laneEnd: number[] = [];
			let lanes = 0;
			idxs.forEach(i => {
				let lane = 0;
				while (laneEnd[lane] !== undefined && laneEnd[lane] > list[i].topMin) { lane++; }
				laneEnd[lane] = list[i].botMin;
				list[i].lane = lane;
				lanes = Math.max(lanes, lane + 1);
			});
			idxs.forEach(i => { list[i].lanes = lanes; });
		});

		return list.map(s => {
			const mins = s.botMin - s.topMin;
			return {
				v: s.v,
				top: this.pct(s.topMin - this.gridStart),
				height: this.pct(mins),
				left: this.round4(s.lane / s.lanes * 100),
				width: this.round4(100 / s.lanes),
				short: mins < 45,
				tiny: mins < 25,
				clipTop: s.topMin > s.startMin,
				clipBottom: s.botMin < s.endMin
			};
		});
	}

	/* ------------------------------------------------------------------ */
	/* Week navigation                                                     */
	/* ------------------------------------------------------------------ */

	get weekLabel(): string {
		const a = this.weekstart;
		const b = this.weekend || this.addDays(this.weekstart, 6);
		if (!a) { return ''; }
		if (a.substring(0, 4) !== b.substring(0, 4)) {
			return this.fmtDate(a, true) + ' – ' + this.fmtDate(b, true);
		}
		if (a.substring(5, 7) !== b.substring(5, 7)) {
			return this.fmtDate(a, false) + ' – ' + this.fmtDate(b, true);
		}
		return Number(a.substring(8, 10)) + ' – ' + this.fmtDate(b, true);
	}

	get isThisWeek(): boolean {
		return this.weekstart === this.mondayOf(this.today());
	}

	prevWeek(): void {
		this.weekstart = this.addDays(this.weekstart, -7);
		this.load();
	}

	nextWeek(): void {
		this.weekstart = this.addDays(this.weekstart, 7);
		this.load();
	}

	thisWeek(): void {
		this.weekstart = this.mondayOf(this.today());
		this.load();
	}

	onLocation(): void {
		this.load();
	}

	/* ------------------------------------------------------------------ */
	/* Selection + actions                                                 */
	/* ------------------------------------------------------------------ */

	select(v: any): void {
		this.selected = v;
		this.internalNote = v.internal_notes || '';
		this.confirmForm.patchValue({ starts_at: v.starts_local, ends_at: v.ends_local, note: '' });
		this.moveForm.patchValue({ starts_at: v.starts_local, ends_at: v.ends_local });
	}

	clearSelection(): void {
		this.selected = null;
	}

	tone(status: string): 'settled' | 'partial' | 'failed' | 'neutral' {
		if (status === 'scheduled') { return 'settled'; }
		if (status === 'requested') { return 'partial'; }
		if (status === 'no_show' || status === 'declined') { return 'failed'; }
		return 'neutral';
	}

	decide(decision: string): void {
		if (!this.selected) { return; }
		const f = this.confirmForm.value;
		this.act('decide', {
			visit_id: this.selected.visit_id,
			decision: decision,
			starts_at: f.starts_at,
			ends_at: f.ends_at,
			note: f.note || ''
		});
	}

	mark(status: string): void {
		if (!this.selected) { return; }
		if (status === 'cancelled' && !window.confirm('Cancel this visit?')) { return; }
		this.act('mark', { visit_id: this.selected.visit_id, status: status });
	}

	move(): void {
		if (!this.selected || !this.moveForm.valid) { return; }
		const f = this.moveForm.value;
		this.act('move', { visit_id: this.selected.visit_id, starts_at: f.starts_at, ends_at: f.ends_at });
	}

	saveNote(): void {
		if (!this.selected) { return; }
		this.act('note', { visit_id: this.selected.visit_id, internal_notes: this.internalNote || '' });
	}

	unblock(): void {
		if (!this.selected || !window.confirm('Remove this block?')) { return; }
		this.act('unblock', { visit_id: this.selected.visit_id });
	}

	/* ------------------------------------------------------------------ */
	/* Block time off                                                      */
	/* ------------------------------------------------------------------ */

	/**
	 * Open the block form, prefilled from wherever the warehouse clicked.
	 *
	 * Clicking an hour cell is the fast path — it is the same gesture as
	 * "nothing can come in then", and typing two datetimes by hand is not.
	 */
	openBlock(date?: string, startMin?: number): void {
		const d = date || this.weekstart;
		const from = startMin === undefined ? 9 * 60 : startMin;
		this.blockForm.patchValue({
			loccode: this.loccode || this.defaultLoccode(),
			title: '',
			starts_at: this.stamp(d, from),
			ends_at: this.stamp(d, from + 60)
		});
		this.dialog.open(this.blockDialog, { width: '460px' });
	}

	/** Click an hour cell in a day column: block that hour. */
	onCellClick(day: CalDay, mark: { hour: number }): void {
		this.openBlock(day.date, mark.hour * 60);
	}

	saveBlock(): void {
		if (!this.blockForm.valid) { return; }
		this.act('blackout', this.blockForm.value);
	}

	/* ------------------------------------------------------------------ */
	/* Published hours                                                     */
	/* ------------------------------------------------------------------ */

	toggleHours(): void {
		this.showHours = !this.showHours;
		if (!this.showHours) {
			this.editing = false;
		}
	}

	get hasHours(): boolean {
		return this.weekly.length > 0 || this.oneoffs.length > 0;
	}

	/** Load one schedule line into the editor. */
	editSchedule(g: any): void {
		this.editMode = 'week';
		this.replaceDays = (g.days || []).slice();
		this.editDays = {};
		this.replaceDays.forEach(d => { this.editDays[d] = true; });
		this.hoursForm.patchValue({
			loccode: g.loccode,
			visit_kind: g.visit_kind,
			specific_date: '',
			start_time: g.start_time,
			end_time: g.end_time,
			slot_minutes: g.slot_minutes,
			capacity: g.capacity,
			active: !!Number(g.active)
		});
		this.editTitle = 'Edit · ' + g.days_label;
		this.editing = true;
	}

	/** Start a new schedule, or a single extra day. */
	newSchedule(mode: 'week' | 'date'): void {
		this.editMode = mode;
		this.replaceDays = [];
		this.editDays = {};
		if (mode === 'week') {
			// Monday to Friday is what a warehouse means by "open", so it is the
			// starting point rather than an empty row.
			[1, 2, 3, 4, 5].forEach(d => { this.editDays[d] = true; });
		}
		this.hoursForm.patchValue({
			loccode: this.loccode || this.defaultLoccode(),
			visit_kind: 'dropoff',
			specific_date: '',
			start_time: '09:00',
			end_time: '16:00',
			slot_minutes: 30,
			capacity: 1,
			active: true
		});
		this.editTitle = mode === 'week' ? 'Add hours' : 'Add a one-off day';
		this.editing = true;
	}

	cancelEdit(): void {
		this.editing = false;
	}

	toggleDay(d: number): void {
		this.editDays[d] = !this.editDays[d];
	}

	get pickedDays(): number[] {
		return Object.keys(this.editDays)
			.filter(k => this.editDays[Number(k)])
			.map(k => Number(k))
			.sort((a, b) => a - b);
	}

	saveSchedule(): void {
		if (!this.hoursForm.valid) { return; }
		const f = this.hoursForm.value;
		const body: any = {
			loccode: f.loccode,
			visit_kind: f.visit_kind,
			start_time: f.start_time,
			end_time: f.end_time,
			slot_minutes: Number(f.slot_minutes),
			capacity: Number(f.capacity),
			active: f.active ? 1 : 0
		};

		if (this.editMode === 'date') {
			if (!f.specific_date) { return; }
			body.window_id = 0;
			body.specific_date = f.specific_date;
			this.act('hours', body);
			return;
		}

		const days = this.pickedDays;
		// No days ticked on an existing schedule means "we are not open then",
		// which is legitimate but destructive enough to ask about.
		if (!days.length) {
			if (!this.replaceDays.length) { return; }
			if (!window.confirm('No days ticked — that unpublishes these hours entirely. Continue?')) { return; }
		}
		body.days = days;
		body.replace_days = this.replaceDays;
		this.act('hoursweek', body);
	}

	/** One-off days delete in place — there is nothing to reconcile. */
	deleteOneoff(w: any): void {
		if (!window.confirm('Delete these hours? Visits already booked inside them are not affected.')) {
			return;
		}
		this.act('hoursdel', { window_id: w.window_id });
	}

	/* ------------------------------------------------------------------ */
	/* Plumbing                                                            */
	/* ------------------------------------------------------------------ */

	/**
	 * Every action reloads the week on success. Confirming, moving or blocking
	 * changes the requests queue, the grid and the open-slot counts at once, and
	 * rebuilding those from one response is cheaper than keeping three in sync.
	 */
	private act(action: string, body: any): void {
		this.saving = true;
		this.warehouseService.warehouseVisitAction(action, body).subscribe((r: any) => {
			this.saving = false;
			if (!r || !r.success) {
				this.say(r && r.message ? r.message : 'Could not save. Please try again.', false);
				return;
			}
			this.dialog.closeAll();
			this.editing = false;
			this.say('Saved.', true);
			this.load();
		});
	}

	private say(msg: string, ok: boolean): void {
		this.message = msg;
		this.messageOk = ok;
	}

	private defaultLoccode(): string {
		return this.locations.length ? String(this.locations[0].loccode) : '01';
	}

	private touchesDay(v: any, date: string): boolean {
		return v.starts_at.substring(0, 10) <= date && v.ends_at.substring(0, 10) >= date;
	}

	private minutesOf(time: string): number {
		const p = String(time || '').split(':');
		return (Number(p[0]) || 0) * 60 + (Number(p[1]) || 0);
	}

	private pct(mins: number): number {
		return this.round4(mins / this.gridSpan * 100);
	}

	private round4(n: number): number {
		return Math.round(n * 10000) / 10000;
	}

	private pad(n: number): string {
		return (n < 10 ? '0' : '') + n;
	}

	clock(min: number): string {
		const h = Math.floor(min / 60) % 24;
		const m = min % 60;
		const h12 = h % 12 === 0 ? 12 : h % 12;
		return h12 + ':' + this.pad(m) + ' ' + (h < 12 ? 'am' : 'pm');
	}

	private hourLabel(h: number): string {
		const h12 = h % 12 === 0 ? 12 : h % 12;
		return h12 + ' ' + (h < 12 ? 'am' : 'pm');
	}

	/** 'YYYY-MM-DDTHH:MM' — what datetime-local and the endpoint both take. */
	private stamp(date: string, min: number): string {
		const d = new Date(date + 'T00:00:00');
		d.setMinutes(d.getMinutes() + Math.round(min));
		return d.getFullYear() + '-' + this.pad(d.getMonth() + 1) + '-' + this.pad(d.getDate())
			+ 'T' + this.pad(d.getHours()) + ':' + this.pad(d.getMinutes());
	}

	private today(): string {
		const d = new Date();
		return d.getFullYear() + '-' + this.pad(d.getMonth() + 1) + '-' + this.pad(d.getDate());
	}

	private mondayOf(date: string): string {
		const d = new Date(date + 'T00:00:00');
		const shift = (d.getDay() + 6) % 7;
		return this.addDays(date, -shift);
	}

	private addDays(date: string, n: number): string {
		const d = new Date(date + 'T00:00:00');
		d.setDate(d.getDate() + n);
		return d.getFullYear() + '-' + this.pad(d.getMonth() + 1) + '-' + this.pad(d.getDate());
	}

	private fmtDate(date: string, withYear: boolean): string {
		const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
		const d = new Date(date + 'T00:00:00');
		return d.getDate() + ' ' + months[d.getMonth()] + (withYear ? ' ' + d.getFullYear() : '');
	}
}
